import type { FilterConfig } from '@/types/filters'

/**
 * LOTE 5: Configurações de filtros para dados específicos de PJ e estagiários
 * 
 * DADOS ESPECÍFICOS DE PJ (4 campos):
 * - CNPJ
 * - Razão Social
 * - Valor Hora PJ
 * - Status Fiscal
 * 
 * DADOS ESPECÍFICOS DE ESTAGIÁRIO (4 campos):
 * - Instituição de Ensino
 * - Curso
 * - Período/Semestre
 * - Supervisor de Estágio
 */

export const LOTE_5_FILTER_CONFIGS: FilterConfig[] = [
  // ===== DADOS ESPECÍFICOS DE PJ =====
  {
    columnId: 'cnpj',
    label: 'CNPJ',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o CNPJ...',
    category: 'Dados PJ'
  },
  {
    columnId: 'companyName',
    label: 'Razão Social',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite a razão social...',
    category: 'Dados PJ'
  },
  {
    columnId: 'hourlyRate',
    label: 'Valor Hora PJ',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Ex: 150.00',
    category: 'Dados PJ'
  },
  {
    columnId: 'taxStatus',
    label: 'Status Fiscal',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'regular', label: 'Regular' },
      { value: 'suspended', label: 'Suspenso' },
      { value: 'cancelled', label: 'Cancelado' },
      { value: 'inapt', label: 'Inapto' },
      { value: 'pending', label: 'Pendente' },
      { value: 'not_verified', label: 'Não Verificado' }
    ],
    placeholder: 'Selecione o status fiscal...',
    category: 'Dados PJ'
  },
  
  // ===== DADOS ESPECÍFICOS DE ESTAGIÁRIO =====
  {
    columnId: 'educationalInstitution',
    label: 'Instituição de Ensino',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains', 'isEmpty', 'isNotEmpty'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione a instituição...',
    category: 'Dados Acadêmicos'
  },
  {
    columnId: 'course',
    label: 'Curso',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains', 'isEmpty', 'isNotEmpty'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione o curso...',
    category: 'Dados Acadêmicos'
  },
  {
    columnId: 'academicPeriod',
    label: 'Período/Semestre',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'greaterThan', 'lessThan'],
    options: [
      { value: '1', label: '1º Período' },
      { value: '2', label: '2º Período' },
      { value: '3', label: '3º Período' },
      { value: '4', label: '4º Período' },
      { value: '5', label: '5º Período' },
      { value: '6', label: '6º Período' },
      { value: '7', label: '7º Período' },
      { value: '8', label: '8º Período' },
      { value: '9', label: '9º Período' },
      { value: '10', label: '10º Período' },
      { value: 'concluded', label: 'Concluído' }
    ],
    placeholder: 'Selecione o período...',
    category: 'Dados Acadêmicos'
  },
  {
    columnId: 'internshipSupervisor',
    label: 'Supervisor de Estágio',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains', 'isEmpty', 'isNotEmpty'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione o supervisor...',
    category: 'Dados Acadêmicos'
  }
]

/**
 * Acessores para campos do Lote 5
 * Campos que precisam de transformação ou acesso especial
 */
export const LOTE_5_FIELD_ACCESSORS = {
  // Dados PJ - campos diretos
  cnpj: (item: any) => item.cnpj,
  companyName: (item: any) => item.companyName,
  hourlyRate: (item: any) => item.hourlyRate,
  taxStatus: (item: any) => item.taxStatus,
  
  // Dados Acadêmicos - campos diretos
  educationalInstitution: (item: any) => item.educationalInstitution,
  course: (item: any) => item.course,
  academicPeriod: (item: any) => item.academicPeriod,
  internshipSupervisor: (item: any) => item.internshipSupervisor,
}

/**
 * Presets estratégicos para Lote 5
 */
export const LOTE_5_PRESETS = [
  {
    id: 'colaboradores_pj_ativos',
    name: 'Pessoa Jurídica Ativos',
    description: 'Colaboradores PJ com status ativo e dados fiscais regulares',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'PJ',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      },
      {
        columnId: 'taxStatus',
        operator: 'equals' as const,
        value: 'regular',
        type: 'select' as const,
        label: 'Status Fiscal',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'pj_valor_hora_alto',
    name: 'PJ - Valor Hora Premium',
    description: 'Colaboradores PJ com valor hora acima de R$ 200',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'PJ',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      },
      {
        columnId: 'hourlyRate',
        operator: 'greaterThan' as const,
        value: 200,
        type: 'number' as const,
        label: 'Valor Hora PJ',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'estagiarios_periodo_avancado',
    name: 'Estagiários - Período Avançado',
    description: 'Estagiários a partir do 5º período',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'INTERN',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      },
      {
        columnId: 'academicPeriod',
        operator: 'greaterThanOrEqual' as const,
        value: '5',
        type: 'select' as const,
        label: 'Período/Semestre',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'estagiarios_por_instituicao',
    name: 'Estagiários por Instituição',
    description: 'Filtrar estagiários de uma instituição específica',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'INTERN',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      }
    ],
    isSystem: true,
    requiresInput: ['educationalInstitution'] // Indica que precisa de input do usuário
  }
] as const